import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  DONATE_STEPS,
  type DonateStep,
  firstOpenStep,
  liveGifts,
  stepState,
  type WizardProgress,
} from '../lib/donateWizard';
import type { DonationRow } from '../lib/donations';
import { useDonorFunding } from '../lib/donorFunding';
import { DonateBoxStep } from './DonateBoxStep';
import { DonateKeyStep } from './DonateKeyStep';
import { DonatePayStep } from './DonatePayStep';
import { DonateRepoStep } from './DonateRepoStep';
import { DonateStepRail, stepTabId } from './DonateStepRail';
import { Modal } from './Modal';

// The donate wizard (arc 101 slice 11, plan doc 05) — project → payment → box →
// key. Shell: it owns which step is showing and derives every step's state; each
// step owns its own writes and its own errors, so a failed one never rolls back
// what an earlier one already gave.
//
// Two rules shape it:
//
//   - **Steps 3-4 unlock on the SERVER's answer.** `useDonorFunding` reads the
//     tier Stripe's webhook writes; `?donated=1` is never consulted. A donor who
//     typed the parameter, and one whose payment is still settling, see the same
//     locked steps — and the wizard re-asks until the server changes its mind.
//   - **Panels are hidden, not swapped.** Once step 3's enroll body has revealed
//     a one-time token, its seat slot is burnt for an hour; unmounting it to look
//     at step 4 would throw that token away.

const NO_GIFTS = { money: false, box: false, key: false } as const;

export type DonateWizardProps = {
  readonly open: boolean;
  readonly token: string | null;
  /** The donor's own gifts — what says which steps this project already has. */
  readonly rows: readonly DonationRow[];
  /** Resume on a project already paid for (the "finish your donation" prompt). */
  readonly resumeProject?: string | null;
  readonly onClose: () => void;
  /** A write landed: reload the gift list, since the row (not the echo) is the truth. */
  readonly onChanged: () => void;
};

export function DonateWizard(props: DonateWizardProps): JSX.Element {
  const [picked, setPicked] = createSignal<string | null>(null);
  // A resumed project is a SEED, not a fallback behind `picked`. "Change
  // project" clears both — behind the prop it would re-resolve on the same tick
  // and re-paint the project the donor just asked to swap, so the button would
  // do nothing at all on the one flow it exists for.
  const [seed, setSeed] = createSignal<string | null>(props.resumeProject ?? null);
  const [step, setStep] = createSignal<DonateStep>('repo');
  // Waiting is a signal rather than a closure over `funding` so the poll's own
  // condition can read the funding it drives without a reference cycle.
  const [waiting, setWaiting] = createSignal(false);

  const project = (): string | null => picked() ?? seed();
  const funding = useDonorFunding(waiting, props.token);

  const gifts = (): { money: boolean; box: boolean; key: boolean } => {
    const target = project();
    return target === null ? NO_GIFTS : liveGifts(props.rows, target);
  };

  const progress = (): WizardProgress => ({
    project: project(),
    funded: funding.funded(),
    boxGiven: gifts().box,
    keyGiven: gifts().key,
  });

  // Re-seed on every OPEN, never on every render: the wizard stays mounted
  // across opens, so the signal's initial value alone would pin the first
  // resume forever — and a seed cleared mid-session must stay cleared.
  createEffect(() => {
    if (props.open) setSeed(props.resumeProject ?? null);
  });

  // Poll only while it can change something: on screen, a project chosen, and
  // the money not yet confirmed.
  createEffect(() => setWaiting(props.open && project() !== null && !funding.funded()));

  // Resume rather than restart: every open lands on the first unfinished step.
  createEffect(() => {
    if (props.open) setStep(firstOpenStep(progress()));
  });

  return (
    <Modal
      open={props.open}
      title={t('donations.wizard_title')}
      desc={t('donations.wizard_desc')}
      // `--xl` (48rem), not `--lg` (32rem) — the four-tab rail with state
      // badges overflows `--lg` at narrow viewports and forces an x-scrollbar.
      size="xl"
      onClose={props.onClose}
    >
      <div class="tabs">
        <DonateStepRail
          steps={DONATE_STEPS}
          current={step()}
          stateOf={(s) => stepState(s, progress())}
          onPick={setStep}
        />

        <div class="tabs__panel">
          <div
            id="donate-panel-repo"
            role="tabpanel"
            aria-labelledby={stepTabId('repo')}
            hidden={step() !== 'repo'}
          >
            <DonateRepoStep
              project={project()}
              onConfirmed={(fullName) => {
                setPicked(fullName);
                setStep('pay');
              }}
              onReset={() => {
                setPicked(null);
                setSeed(null);
              }}
            />
          </div>

          <Show when={project()}>
            {(target) => (
              <>
                <div
                  id="donate-panel-pay"
                  role="tabpanel"
                  aria-labelledby={stepTabId('pay')}
                  hidden={step() !== 'pay'}
                >
                  <DonatePayStep
                    project={target()}
                    token={props.token}
                    funded={funding.funded()}
                    pending={!funding.funded() && gifts().money}
                    onCreated={props.onChanged}
                  />
                </div>
                <div
                  id="donate-panel-box"
                  role="tabpanel"
                  aria-labelledby={stepTabId('box')}
                  hidden={step() !== 'box'}
                >
                  <DonateBoxStep
                    project={target()}
                    token={props.token}
                    open={props.open}
                    done={gifts().box}
                    onDonated={props.onChanged}
                  />
                </div>
                <div
                  id="donate-panel-key"
                  role="tabpanel"
                  aria-labelledby={stepTabId('key')}
                  hidden={step() !== 'key'}
                >
                  <DonateKeyStep
                    project={target()}
                    token={props.token}
                    done={gifts().key}
                    onDonated={props.onChanged}
                  />
                </div>
              </>
            )}
          </Show>
        </div>
      </div>

      <Show when={project() !== null && !funding.funded()}>
        <p class="field__desc">{t('donations.step_locked_note')}</p>
      </Show>
    </Modal>
  );
}
